import type { MultiSelectOption } from "@/components/ui/MultiSelect";
import type { FilterValues } from "@/components/ui/FilterBar/types";
import type { CotacaoCard, KanbanComprasData, PedidoCard, SolicitacaoCard } from "@/services/suprimentos";

/** Nomes únicos de fornecedor nas cotações e pedidos (para o MultiSelect). */
export function buildSupplierOptionsFromKanban(data: KanbanComprasData | null): MultiSelectOption[] {
  if (!data) return [];
  const seen = new Set<string>();
  const ordered: string[] = [];
  const push = (raw: string | undefined) => {
    const t = String(raw ?? "").trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    ordered.push(t);
  };
  for (const c of data.cotacoes) push(c.supplierName);
  for (const p of data.pedidosSemFinanceiro) push(p.supplier);
  for (const p of data.pedidosGeradoFinanceiro) push(p.supplier);
  ordered.sort((a, b) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }));
  return ordered.map((name) => ({ value: name, label: name }));
}

function supplierMatchesSelection(name: string | undefined, f: FilterValues): boolean {
  const raw = f.supplierFilter as unknown;
  const sel = Array.isArray(raw)
    ? (raw as string[]).map((s) => String(s).trim()).filter(Boolean)
    : typeof raw === "string" && raw.trim()
      ? [raw.trim()]
      : [];
  if (sel.length === 0) return true;
  const h = String(name ?? "").trim();
  if (!h) return false;
  return sel.some((s) => s === h || s.toLowerCase() === h.toLowerCase());
}

function itemMatchesSelection(haystacks: string[], f: FilterValues): boolean {
  const raw = f.itemFilter as unknown;
  const sel = Array.isArray(raw)
    ? (raw as string[]).map((s) => String(s).trim()).filter(Boolean)
    : typeof raw === "string" && raw.trim()
      ? [raw.trim()]
      : [];
  if (sel.length === 0) return true;
  const hs = haystacks.map((h) => String(h ?? "").trim()).filter(Boolean);
  if (hs.length === 0) return false;
  return sel.some((s) =>
    hs.some((h) => fieldMatchesFilter(h, s))
  );
}

/** Contém texto ou, se houver 2+ dígitos no filtro, compara só os dígitos (ex.: "4005" no número do pedido). */
function fieldMatchesFilter(field: unknown, filterRaw: string): boolean {
  const f = String(filterRaw ?? "").trim();
  if (!f) return true;
  const h = String(field ?? "").trim();
  if (!h) return false;
  const fl = f.toLowerCase();
  const hl = h.toLowerCase();
  if (hl.includes(fl)) return true;
  const fd = f.replace(/\D/g, "");
  if (fd.length >= 2) {
    const hd = h.replace(/\D/g, "");
    if (hd.includes(fd)) return true;
  }
  return false;
}

function solicitacaoMatches(c: SolicitacaoCard, f: FilterValues): boolean {
  const itemPreview = Array.isArray(c.itemsPreview) ? c.itemsPreview : [];
  if (!itemMatchesSelection(itemPreview, f)) return false;
  const qPr = f.purchaseRequestFilter ?? "";
  if (!String(qPr).trim()) return true;
  const id = String(c.purchaseRequestId ?? "").trim();
  const want = String(qPr).trim().replace(/^sc\s*/i, "").trim();
  return fieldMatchesFilter(id, want);
}

function cotacaoMatches(c: CotacaoCard, f: FilterValues): boolean {
  if (!itemMatchesSelection([c.label], f)) return false;
  const qCt = f.quotationFilter ?? "";
  if (String(qCt).trim()) {
    const okId = fieldMatchesFilter(c.purchaseQuotationId, qCt);
    const okLabel = fieldMatchesFilter(c.label, qCt);
    const okSup = fieldMatchesFilter(c.supplierName, qCt);
    if (!okId && !okLabel && !okSup) return false;
  }
  if (!supplierMatchesSelection(c.supplierName, f)) return false;
  return true;
}

function pedidoMatches(p: PedidoCard, f: FilterValues): boolean {
  const itemPreview = Array.isArray(p.itemsPreview) ? p.itemsPreview : [];
  if (!itemMatchesSelection(itemPreview, f)) return false;
  const qOrd = f.orderFilter ?? "";
  if (String(qOrd).trim()) {
    const want = String(qOrd).trim().replace(/^pedido\s*/i, "").trim();
    const okNum = fieldMatchesFilter(p.orderNumber, want);
    const okId = fieldMatchesFilter(p.purchaseOrderId, want);
    if (!okNum && !okId) return false;
  }
  if (!supplierMatchesSelection(p.supplier, f)) return false;
  return true;
}

/** Filtra o payload do Kanban (client-side) pelos campos de texto de Suprimentos. */
export function filterKanbanComprasData(data: KanbanComprasData, f: FilterValues): KanbanComprasData {
  return {
    solPendente: data.solPendente.filter((c) => solicitacaoMatches(c, f)),
    solAprovada: data.solAprovada.filter((c) => solicitacaoMatches(c, f)),
    cotacoes: data.cotacoes.filter((c) => cotacaoMatches(c, f)),
    pedidosSemFinanceiro: data.pedidosSemFinanceiro.filter((p) => pedidoMatches(p, f)),
    pedidosGeradoFinanceiro: data.pedidosGeradoFinanceiro.filter((p) => pedidoMatches(p, f)),
  };
}
